import { observable } from "@legendapp/state";
import {
  RUN_PROGRESS_EVENT,
  type RunProgressEvent,
} from "@repo/shared/types/run-progress";
import { normalizeColumnsConfig } from "@repo/shared/utils/merge-columns-configs";
import {
  pruneColumnsConfig,
  pruneRiftConfig,
} from "@repo/shared/utils/prune-stale-columns";
import type * as Ably from "ably";

import { syncedCrudPersist } from "~/libs/legend-state";
import { getOrgChannel } from "~/libs/pubsub.lib";
import { trpc } from "~/libs/trpc.lib";
import { appDataReady$ } from "../app-data-ready";
import { boardFilters$ } from "../board-filters/board-filters.crud";
import {
  hasActiveBoardRefs,
  rewarmDataset,
} from "../board-payloads/board-payloads.actions";
import { isSnapshotOrigin } from "../board-snapshot/snapshot-origins";
import {
  isDatasetRiftActive,
  removeRiftQueriesForDataset,
} from "../dataset-rifts/dataset-rifts.query";
import {
  clearManualRefreshPending,
  clearRefreshGuard,
} from "./datasets.actions";
import type { Dataset, DatasetsLocal } from "./datasets.types";
import {
  getLocalUploadDatasets,
  isLocalUploadDataset,
  mergeLocalUploadDataset,
  removeLocalUploadDataset,
} from "./local-uploads";
import { ensureRunPayload } from "./rift-dataset-payload.query";
import { clearRunProgress, recordRunProgress } from "./run-progress";

/** Client-only state per dataset — never synced to server or IDB. */
export const datasetsLocal$ = observable<Record<string, DatasetsLocal>>({});

/**
 * Removes board filter columns for a dataset whose accessor no longer exists
 * in the published columns. Deletes filters that lose all their columns.
 */
const cleanupStaleFilterColumns = (datasetId: string) => {
  const columns = datasets$[datasetId]?.columns.get();
  if (!columns) return;

  const validAccessors = new Set(Object.keys(columns));
  const all = boardFilters$.get() ?? {};

  for (const filter of Object.values(all)) {
    if (!filter) continue;
    const hasStale = filter.columns.some(
      (c) => c.datasetId === datasetId && !validAccessors.has(c.accessor),
    );
    if (!hasStale) continue;

    const kept = filter.columns.filter(
      (c) => c.datasetId !== datasetId || validAccessors.has(c.accessor),
    );
    if (kept.length === 0) {
      boardFilters$[filter.id].delete();
    } else {
      boardFilters$[filter.id].columns.set(kept);
    }
  }
};

/**
 * Replaces columnsConfig with a pruned copy that only contains keys
 * matching the current published columns.
 */
const cleanupStaleColumnsConfig = (datasetId: string) => {
  const columns = datasets$[datasetId]?.columns.get();
  const columnsConfig = datasets$[datasetId]?.columnsConfig.get();
  if (!columns || !columnsConfig) return;

  const validColumns = new Set(Object.keys(columns));
  const pruned = pruneColumnsConfig(columnsConfig, validColumns);

  if (Object.keys(pruned).length !== Object.keys(columnsConfig).length) {
    datasets$[datasetId].columnsConfig.set(pruned);
  }
};

/**
 * Replaces riftConfig with a pruned copy that removes column references
 * no longer in the published schema.
 */
const cleanupStaleRiftConfig = (datasetId: string) => {
  const columns = datasets$[datasetId]?.columns.get();
  const riftConfig = datasets$[datasetId]?.riftConfig.get();
  if (!columns || !riftConfig) return;

  const validColumns = new Set(Object.keys(columns));
  const pruned = pruneRiftConfig(riftConfig, validColumns);

  if (pruned) {
    datasets$[datasetId].riftConfig.set(pruned);
  }
};

export const datasets$ = observable(
  syncedCrudPersist<Dataset>({
    persist: {
      name: "datasets",
    },
    waitFor: appDataReady$,
    // Merge the local list with the server list
    list: async () => {
      const datasets = await trpc.datasets.list.query();
      // Upconvert legacy `{ formatType, formatOptions }` column configs to the
      // `{ format }` discriminated union on load.
      const rows = datasets.map((dataset) => ({
        ...dataset,
        columnsConfig: normalizeColumnsConfig(dataset.columnsConfig),
      }));
      // `mode: "set"` replaces the store on every refresh, so client-only
      // uploads have to be re-merged from their registry each time.
      return [...rows, ...getLocalUploadDatasets()];
    },
    update: async ({ id, ...input }) => {
      if (!id) return;
      if (isLocalUploadDataset(id)) {
        mergeLocalUploadDataset(id, input);
        return;
      }
      if (isSnapshotOrigin(id)) return;

      await trpc.datasets.update.mutate({
        id,
        ...input,
      });
    },
    create: async (input) => {
      if (isSnapshotOrigin(input.id)) return input;
      return await trpc.datasets.create.mutate(input);
    },
    delete: async (input) => {
      if (!input.id) return;
      if (isLocalUploadDataset(input.id)) {
        removeLocalUploadDataset(input.id);
        return;
      }
      if (isSnapshotOrigin(input.id)) return;

      await trpc.datasets.delete.mutate({
        datasetId: input.id,
      });
    },
    subscribe: ({ refresh, update }) => {
      const channel = getOrgChannel();
      if (!channel) return;

      const handler = (message: Ably.Message) => {
        const items = Array.isArray(message.data)
          ? message.data
          : [message.data];

        const filtered = items.filter((item) => !isSnapshotOrigin(item?.id));
        if (filtered.length === 0) return;

        update({ mode: "merge", value: filtered });

        if (filtered.some((item) => "runId" in item)) {
          refresh();
        }
      };

      // Ephemeral counters for an in-flight streaming publish. Kept out of the
      // CRUD payload entirely — they are display-only and must never be
      // persisted or written back to the server.
      const progressHandler = (message: Ably.Message) => {
        const event = message.data as RunProgressEvent | undefined;
        if (!event?.id || isSnapshotOrigin(event.id)) return;
        recordRunProgress(event);
      };

      channel.subscribe("datasets", handler);
      channel.subscribe(RUN_PROGRESS_EVENT, progressHandler);

      return () => {
        channel.unsubscribe("datasets", handler);
        channel.unsubscribe(RUN_PROGRESS_EVENT, progressHandler);
      };
    },
  }),
);

/**
 * True when a mounted board or a live rift query observer is currently
 * rendering this dataset. Only then is it worth deferring a run transition to
 * keep showing the old payload while the new one downloads.
 */
const isDatasetDisplaying = (datasetId: string): boolean => {
  if (hasActiveBoardRefs(datasetId)) return true;
  return isDatasetRiftActive(datasetId);
};

/**
 * Invalidates the rift cache and prunes persisted column references the new
 * publish dropped, keyed to the run now being rendered. Run when a run is
 * *adopted* (immediately for first publish / cold hydration / background
 * datasets, or after the new payload downloads for on-screen ones).
 */
const runRunCleanups = (datasetId: string, runId: string) => {
  removeRiftQueriesForDataset(datasetId, { keepRunId: runId });
  cleanupStaleFilterColumns(datasetId);
  cleanupStaleColumnsConfig(datasetId);
  cleanupStaleRiftConfig(datasetId);
};

/**
 * Downloads + registers the newly published run's payload in the background,
 * then flips `activeRunId` to it and runs the stale-state cleanups. Until then
 * the UI keeps rendering the previously-active payload. Guards against
 * out-of-order adoption (rapid re-publishes) by only flipping when `newRunId`
 * is still the server run. Because runs are immutable, the outgoing payload
 * never disappears out from under an open board while the new one downloads.
 */
const adoptDatasetRun = (datasetId: string, newRunId: string) => {
  const finish = () => {
    if (datasets$[datasetId].runId.get() !== newRunId) return;
    datasetsLocal$[datasetId].activeRunId.set(newRunId);
    runRunCleanups(datasetId, newRunId);
  };
  ensureRunPayload({ runId: newRunId }).then(finish, finish);
};

/**
 * Real side effects on `runId` transitions. For datasets currently on
 * screen, the new run is *deferred*: `activeRunId` stays pinned to the outgoing
 * payload so blocks keep rendering data while the new Arrow file downloads, and
 * the transition (cleanups + `activeRunId` flip) happens in `adoptDatasetRun`
 * once it's ready. Otherwise (first publish, cold hydration, or a dataset
 * nothing is displaying) the run is adopted immediately. Rift recomputation is
 * handled reactively by `useBlockRift` / `useDatasetRift` in the consumers.
 *
 * Matches both per-key `runId` changes (`path = [datasetId,
 * "runId"]`) and whole-dataset inserts from a fresh CRUD `list()`
 * (`path = [datasetId]` with `prevAtPath === undefined`). The latter is the
 * cold-cache hydration path that previously slipped through.
 */
datasets$.onChange(({ changes }) => {
  for (const change of changes) {
    let datasetId: string | null = null;
    let newRunId: string | null | undefined;
    let prevRunId: string | null | undefined;
    // True only for an in-place `runId` mutation (a real publish), not
    // the whole-dataset insert produced by cold-cache hydration.
    let isRunTransition = false;

    // A real server status change has arrived — the manual-refresh spinner is
    // now driven by `status` (RUNNING) or the run is over (IDLE/FAILED), so the
    // client-only "pending" flag has served its purpose.
    if (change.path[1] === "status") {
      const statusDatasetId = change.path[0] as string;
      if (!isSnapshotOrigin(statusDatasetId)) {
        clearManualRefreshPending(statusDatasetId);
        // The publish is over (or has restarted), so any streamed counters
        // describe a run nothing is waiting on any more.
        if (change.valueAtPath !== "RUNNING") {
          clearRunProgress(statusDatasetId);
        }
      }
      continue;
    }

    if (change.path[1] === "runId") {
      datasetId = change.path[0] as string;
      newRunId = change.valueAtPath as string | null;
      prevRunId = change.prevAtPath as string | null;
      isRunTransition = true;
    } else if (
      change.path.length === 1 &&
      change.prevAtPath === undefined &&
      change.valueAtPath
    ) {
      const dataset = change.valueAtPath as Dataset;
      datasetId = dataset.id;
      newRunId = dataset.runId;
    }

    if (!datasetId) continue;
    if (isSnapshotOrigin(datasetId)) continue;
    clearRefreshGuard(datasetId);
    if (!newRunId) continue;

    const shouldDefer =
      isRunTransition &&
      typeof prevRunId === "string" &&
      prevRunId.length > 0 &&
      prevRunId !== newRunId &&
      isDatasetDisplaying(datasetId);

    if (shouldDefer) {
      // Pin the rendered version to the outgoing run if nothing has pinned it
      // yet (a prior in-flight adoption may already hold an older one), then
      // adopt the new run once its payload is downloaded + registered.
      if (datasetsLocal$[datasetId].activeRunId.get() == null) {
        datasetsLocal$[datasetId].activeRunId.set(prevRunId);
      }
      adoptDatasetRun(datasetId, newRunId);
      continue;
    }

    // Immediate adoption: first publish, cold hydration, or a dataset nothing
    // is currently displaying — there's no old data worth preserving.
    datasetsLocal$[datasetId].activeRunId.set(newRunId);
    runRunCleanups(datasetId, newRunId);
    // Proactively warm the new payload version only on a real publish. On
    // cold-cache hydration the board-mount path and on-demand consumers
    // already fetch the payload, so warming here would be redundant.
    if (isRunTransition) {
      rewarmDataset(datasetId);
    }
  }
});
